import type { CalendarEventData, CalendarFrequency } from "@qendo/calendar";
import { OptionType } from "@qendo/types";
import type { TimeZone } from "@qendo/util";
import { type DateArg, isAfter } from "date-fns";
import { useEffect, useId, useState } from "react";
import { Platform } from "react-native";
import { Button } from "~/components/Button";
import { FrequencyCombo } from "~/components/form/calendar/FrequencyCombo";
import { ComboBox } from "~/components/form/combo-box/ComboBox";
import { OptionSelector } from "~/components/form/combo-box/OptionSelector";
import { TimezoneSelector } from "~/components/form/combo-box/TimezoneSelector";
import { DatePicker } from "~/components/form/date-picker/DatePicker";
import { DateTimePicker } from "~/components/form/date-picker/DateTimePicker";
import { TimePicker } from "~/components/form/date-picker/TimePicker";
import { FieldGroup } from "~/components/form/FieldGroup";
import { MediaPicker } from "~/components/form/gallery/MediaPicker";
import { NumberInput } from "~/components/form/NumberInput";
import { TextArea } from "~/components/form/TextArea";
import { TextInput } from "~/components/form/TextInput";
import { Columns } from "~/components/layout/Columns";
import { FullPageModal, type FullPageModalProps } from "~/components/layout/modal/FullPageModal";
import { useErrorBag } from "~/providers/ErrorBagProvider";
import { useFeedback } from "~/providers/FeedbackProvider";
import { useJournal } from "~/providers/JournalProvider";
import { empty, filled } from "~/util";
import { elementsMatch } from "~/util/array";
import { currentTimezone, formatDateString, formatDateTimeString, formatTimeString, mergeDateTime } from "~/util/dates";
import type { Numeric } from "~/util/string";

type CalendarFormProps = {
    event?: CalendarEventData;
    onRequestClose: () => void;
} & Partial<FullPageModalProps>;

enum Until {
    forever = "Forever",
    date = "A specific date",
    times = "A number of times",
}

export const CalendarForm = ({ event, onRequestClose, ...modalProps }: CalendarFormProps) => {
    const { canUpdateCalendar, storeEvent } = useJournal();
    const current_timezone = currentTimezone();
    const exists = Boolean(event?.id);
    const [option, setOption] = useState<string | null>(event?.option_id ?? null);
    const [title, setTitle] = useState<string>(event?.title ?? "");
    const [notes, setNotes] = useState<string>(event?.notes ?? "");
    const [location, setLocation] = useState<string>(event?.location ?? "");
    const [start, setStart] = useState<DateArg<Date> | null>(
        event?.start_at ? formatDateTimeString(event.start_at) : null,
    );
    const [end, setEnd] = useState<DateArg<Date> | null>(event?.end_at ? formatTimeString(event.end_at) : null);
    const [timezone, setTimezone] = useState<TimeZone | null>(event?.time_zone ?? current_timezone);
    const [frequency, setFrequency] = useState<CalendarFrequency | null>(event?.recurrence?.frequency ?? null);
    const [interval, setInterval] = useState<Numeric>(event?.recurrence?.interval ?? 1);
    const [until, setUntil] = useState<Until>(
        (event?.recurrence?.end_date && Until.date) || (event?.recurrence?.occurrence && Until.times) || Until.forever,
    );
    const [times, setTimes] = useState<Numeric>(event?.recurrence?.occurrence ?? 2);
    const [ends, setEnds] = useState<DateArg<Date> | null>(
        event?.recurrence?.end_date ? formatDateString(event.recurrence.end_date) : null,
    );
    const [photographs, setPhotographs] = useState<string[]>(event?.photograph_ids ?? []);
    const errorBag = useErrorBag();
    const id = useId();

    // biome-ignore lint/correctness/useExhaustiveDependencies(errorBag.forget): cleanup
    useEffect(() => {
        errorBag.forget([
            "option_id",
            "title",
            "notes",
            "location",
            "start_at",
            "end_at",
            "interval",
            "until",
            "end_date",
            "times",
        ]);
    }, []);

    const { saving, setSaving } = useFeedback();
    const [dirty, setDirty] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies(current_timezone): external
    useEffect(() => {
        setDirty(
            (isDirty) =>
                isDirty ||
                option !== (event?.option_id ?? null) ||
                location !== (event?.title ?? "") ||
                notes !== (event?.notes ?? "") ||
                location !== (event?.location ?? "") ||
                start !== (event?.start_at ? formatDateTimeString(event.start_at) : null) ||
                end !== (event?.end_at ? formatTimeString(event.end_at) : null) ||
                timezone !== (event?.time_zone ?? current_timezone) ||
                frequency !== (event?.recurrence?.frequency ?? null) ||
                `${interval}` !== `${event?.recurrence?.interval ?? 1}` ||
                times !== `${event?.recurrence?.occurrence ?? 2}` ||
                ends !== (event?.recurrence?.end_date ? formatDateString(event.recurrence.end_date) : null) ||
                !elementsMatch(photographs, event?.photograph_ids ?? []),
        );
    }, [event, option, location, notes, photographs, start, end, timezone, frequency, interval, times, ends]);

    const canSave =
        canUpdateCalendar() &&
        filled(option) &&
        filled(title) &&
        filled(start) &&
        (empty(end) || isAfter(mergeDateTime(start, end), start)) &&
        (empty(frequency) ||
            (until === Until.date && filled(ends) && isAfter(ends, start)) ||
            [Until.forever, Until.times].includes(until));

    const doSave = () => {
        if (saving || !canSave) {
            return;
        }

        setSaving(id);
    };

    const commit = () => {
        if (!canSave) {
            setSaving(false);
            return;
        }

        const timing_payload = frequency
            ? {
                  frequency,
                  interval,
                  times: until === Until.times ? times : null,
                  repeat_until: until === Until.date ? formatDateString(ends as DateArg<Date>) : null,
              }
            : {};

        const id_payload = event?.id ? { id: event.id } : {};

        const payload = {
            ...id_payload,
            option_id: option,
            title,
            notes,
            location,
            start_at: formatDateTimeString(start),
            end_at: filled(end) ? formatDateTimeString(mergeDateTime(start, end)) : null,
            all_day: empty(end),
            time_zone: timezone,
            ...timing_payload,
            photograph_ids: photographs,
        };

        storeEvent(payload)
            .then(onRequestClose)
            .finally(() => setSaving(false));
    };

    const is_saving = saving === id;

    // biome-ignore lint/correctness/useExhaustiveDependencies(commit): callback
    useEffect(() => {
        is_saving && commit();
    }, [is_saving]);

    return (
        <FullPageModal
            {...modalProps}
            cancelable
            dirty={dirty}
            title={`${exists ? "Update" : "Add"} event`}
            onRequestClose={onRequestClose}
            headerRight={() => (
                <Button disabled={!canSave} title="Save" size="sm" onPress={doSave} loading={is_saving} />
            )}
        >
            <FieldGroup>
                <OptionSelector
                    name="option_id"
                    label="Event type"
                    type={OptionType.calendar_events}
                    value={option}
                    onItemSelected={setOption}
                    disabled={is_saving}
                />
                <TextInput name="title" label="Title" value={title} onChangeText={setTitle} disabled={is_saving} />
                <TextInput
                    name="location"
                    label="Location"
                    value={location}
                    onChangeText={setLocation}
                    disabled={is_saving}
                    optional
                />
                <Columns
                    columns={Platform.select({ android: 2, default: 1 })}
                    items={[
                        () => (
                            <DateTimePicker
                                name="start_at"
                                label="Starts"
                                value={start}
                                onDateSelected={setStart}
                                disabled={is_saving}
                            />
                        ),
                        () => (
                            <TimePicker
                                name="ends_at"
                                label="Ends"
                                value={end}
                                placeholder="All day"
                                onDateSelected={setEnd}
                                disabled={is_saving}
                                optional
                                clearable
                                min={start}
                            />
                        ),
                    ]}
                />
                <TimezoneSelector
                    name="time_zone"
                    value={timezone}
                    onItemSelected={setTimezone}
                    date={start}
                    placeholder="Select time zone"
                />
                {frequency ? (
                    <Columns
                        columns={2}
                        items={[
                            () => <FrequencyCombo value={frequency} onItemSelected={setFrequency} />,
                            () => (
                                <NumberInput
                                    name="interval"
                                    label="Interval"
                                    min={1}
                                    empty={1}
                                    value={interval}
                                    onChangeText={setInterval}
                                    asInt
                                />
                            ),
                            () => (
                                <ComboBox<Until>
                                    name="until"
                                    label="Until"
                                    items={Object.values(Until)}
                                    value={until}
                                    onItemSelected={setUntil}
                                />
                            ),
                            (until === Until.date &&
                                (() => (
                                    <DatePicker
                                        value={ends}
                                        onDateSelected={setEnds}
                                        name="end_date"
                                        min={start}
                                        label="End date"
                                    />
                                ))) ||
                                (until === Until.times &&
                                    (() => (
                                        <NumberInput
                                            name="times"
                                            label="Times"
                                            min={2}
                                            empty={2}
                                            value={times}
                                            onChangeText={setTimes}
                                            asInt
                                        />
                                    ))) ||
                                null,
                        ]}
                    />
                ) : (
                    <FrequencyCombo value={frequency} onItemSelected={setFrequency} />
                )}
            </FieldGroup>
            <FieldGroup>
                <TextArea name="notes" label="Notes" value={notes} onChangeText={setNotes} optional />
                <MediaPicker value={photographs} onSelectionChanged={setPhotographs} disabled={is_saving} />
            </FieldGroup>
        </FullPageModal>
    );
};
